import React from 'react';

import { cn } from '@express-labs/raven-tools';

type SharedBlurbProps<C extends React.ElementType> = {
  /** Blurb is a polymorphic component. Meaning, you can set the HTML tag to be rendered. */
  tag?: C;
  /** Any additional css classes to append to the classes that are applied by automatically the `kind` prop. */
  className?: string;
  /** The kind prop applies global typography class names. See the typography section of Storybook */
  kind?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'eyebrow'
    | 'largeLink'
    | 'smallLink';
} & React.ComponentPropsWithoutRef<C>;

/**
 * When isHtml prop is false or undefined, we are able to display children.
 */
type SafeBlurbProps = {
  isHtml?: false;
  html?: never;
  children: React.ReactNode;
};

/**
 * If isHtml prop is true, render the contents of the `html` prop as dangerously set raw html and
 * ignore any children.
 */
type DangerousBlurbProps = {
  /** When `isHtml` is truthy any raw HTML in the `html` prop is displayed and the `children` prop is ignored.  */
  isHtml: true;
  html: string;
  children?: never;
};

/**
 * A utility component for displaying text formatted using the officially supported Raven
 * typography.  It is also useful for wrapping any "authored content" that contains raw html. For
 * example, use Blurb to display any content created in a rich text editor, like one in Adobe
 * Experience Manager.
 *
 * ```jsx
 * // Examples of use:
 * const A = <Blurb>hello</Blurb>;
 * const B = <Blurb tag="span" kind="tertiary">hello</Blurb>;
 * const C = <Blurb tag="p" isHtml html="<strong>hello</strong>"></Blurb>;
 * ```
 */
export type BlurbProps<C extends React.ElementType> = SharedBlurbProps<C> &
  (SafeBlurbProps | DangerousBlurbProps);

function Blurb<C extends React.ElementType = 'div'>({
  className,
  isHtml = false,
  html,
  kind = 'primary',
  tag,
  ...otherProps
}: BlurbProps<C>) {
  const classMap = {
    h1: 'rvnH1',
    h2: 'rvnH2',
    h3: 'rvnH3',
    h4: 'rvnH4',
    h5: 'rvnH5',
    primary: 'bodyPrimary',
    secondary: 'bodySecondary',
    tertiary: 'bodyTertiary',
    eyebrow: 'rvnEyebrow',
    largeLink: 'rvnLargeLink',
    smallLink: 'rvnSmallLink',
  };
  const classes = cn(className, classMap[kind]);
  const Tag = tag ?? 'div';

  if (isHtml && html) {
    return (
      <Tag
        className={classes}
        dangerouslySetInnerHTML={{ __html: html }}
        {...otherProps}
      />
    );
  } else {
    const { children, ...filteredProps } = otherProps;
    return (
      <Tag className={classes} {...filteredProps}>
        {children}
      </Tag>
    );
  }
}

Blurb.displayName = 'Blurb';

export default Blurb;
